import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { createStyles, makeStyles, Theme, ButtonBase } from '@material-ui/core';
import { AutocompleteCloseReason } from '@material-ui/lab/Autocomplete';
import * as Colors from 'src/theme/colors';
import { CRM_OPERATIONS, User } from 'src/constants';
import { UserAvatar } from 'src/components/User';
import { AssigneeList } from 'src/components/Assignee/AssigneeList';
import { AssigneeDropDown } from 'src/components/Assignee/AssigneeDropDown';
import { AssigneeTooltipWrapper } from 'src/components/Assignee/AssigneeTooltipWrapper';
import { updateCompany } from 'src/store/clients/actions';
import { AssigneeDropDownOptions } from 'src/store/clients/types';
import { RootState } from 'src/store';
import * as UserUtils from 'src/utils/UserUtils';
import * as Constants from 'src/constants';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(0.75),
      borderRadius: 4,
      '&:hover': {
        cursor: 'pointer',
        backgroundColor: Colors.HoverNonBorderTableEditableItemBackground,
      },
      '& > *': {
        display: 'grid',
      },
    },
  }),
);

interface LeadProps {
  lead: User;
  companyId?: string;
  readOnly?: boolean;
}

export const Lead = ({ lead, companyId, readOnly = false }: LeadProps) => {
  const userPermissions = useSelector(
    (state: RootState) => state.user.data?.permissions,
  );
  const classes = useStyles();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClickLeadDropDown = (event: React.MouseEvent<HTMLElement>) => {
    if (readOnly) return;
    const canChangeLead = UserUtils.hasOperation(
      Constants.PERM_MOD_CRM,
      CRM_OPERATIONS.CHANGE_ASSIGNEE,
      userPermissions,
    );

    if (!canChangeLead) return;
    setAnchorEl(event.currentTarget);
  };

  const handleCloseLeadDropDown = (
    _?: React.ChangeEvent<HTMLInputElement>,
    reason?: AutocompleteCloseReason,
  ) => {
    if (reason === 'toggleInput') {
      return;
    }
    setAnchorEl(null);
  };

  const leadDropDownOpen = Boolean(anchorEl);
  const leadDropDownId = leadDropDownOpen ? 'lead-droprown' : undefined;

  const handleLeadChange = (currentLead: AssigneeDropDownOptions) => {
    const sendData = {
      companyId,
      leadUserID: currentLead.id,
      previousLeadUserID: lead.id,
    };

    handleCloseLeadDropDown();

    const updateCompanyOptions = {
      showSuccessMessage: false,
      isOptimisticChange: true,
    };
    if (!isEmpty(sendData)) {
      dispatch(updateCompany(sendData, updateCompanyOptions));
    }
  };

  if (!lead) {
    return <div />;
  }

  return (
    <div>
      <AssigneeTooltipWrapper
        enterDelay={500}
        enterNextDelay={500}
        title={<AssigneeList title="Lead" option={lead} />}
        data-testid="lead-tooltip"
      >
        <ButtonBase
          className={classes.root}
          aria-describedby={leadDropDownId}
          onClick={handleClickLeadDropDown}
        >
          <UserAvatar
            type="image"
            avatarUrl={lead?.fields.picture}
            name={`${lead?.fields.given_name} ${lead?.fields.family_name}`}
            fallbackColor={lead?.fields.fallbackColor}
            avatarSize="mini"
          />
        </ButtonBase>
      </AssigneeTooltipWrapper>
      {leadDropDownOpen && (
        <AssigneeDropDown
          assigneeDropDownId={leadDropDownId}
          open={leadDropDownOpen}
          anchorEl={anchorEl}
          onClose={handleCloseLeadDropDown}
          placeholder="Set lead..."
          isMultiple={false}
          lead={lead}
          onChange={handleLeadChange}
          disableCloseOnSelect
        />
      )}
    </div>
  );
};
